import { useRef } from 'react';
import { DisclosureExpand, ErrorStatus, WarningStatus } from '@sas/react-svg';
import cx from 'classnames';
import { setAlpha } from '../../../design-tokens';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderSlotWithComponentFallback } from '../../../slot';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { useDensity } from '../../Density';
import { Icon } from '../../Icon';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { InvisibleText } from '../../InvisibleText';
import bundle from '../NovaComponents-Select-gui-icu.properties';
import cn from '../Select.css.json';
import '../Select.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('Select');

/**
 * @internal
 */
export const SelectBoxDesign = props => {
  const localize = useTextLocalizer();
  const focusable = props.focusable ?? true;
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokens = {
    ...getSelectDesignTokens(dt, {
      readOnly: props.readOnly ?? false,
      status: props.status,
      theme: props.theme,
      font: props.font,
      density
    }),
    ...props.futureTheme?.(dt)
  };
  const showStatus = props.status === 'alert' || props.status === 'warning';
  const showStatusIcon = !props.hasFocus && showStatus;
  const statusIcon = showStatusIcon ? props.status === 'alert' ? /*#__PURE__*/_jsx(Icon, {
    icon: ErrorStatus,
    color: "alert"
  }) : /*#__PURE__*/_jsx(Icon, {
    icon: WarningStatus,
    color: "warning"
  }) : undefined;
  const textRef = useRef(null);
  const setTruncationRef = useTruncationTooltip({
    tooltip: props.tooltip,
    truncateRef: textRef
  });
  const invisibleTextId = useHtmlId();
  const innerTextId = useHtmlId();
  const ariaDescribedBy = props.required ? [invisibleTextId] : [];
  if (Array.isArray(props.ariaDescribedBy)) {
    ariaDescribedBy.push(...props.ariaDescribedBy);
  } else if (props.ariaDescribedBy !== undefined) {
    ariaDescribedBy.push(props.ariaDescribedBy);
  }
  const ariaLabelledBy = [];
  if (Array.isArray(props.ariaLabelledBy)) {
    ariaLabelledBy.push(...props.ariaLabelledBy);
  } else if (props.ariaLabelledBy !== undefined) {
    ariaLabelledBy.push(props.ariaLabelledBy);
  }
  const itemIconId = useHtmlId();
  const ariaLabel = [];
  const getIconA11yInfo = useIconA11yInfo();
  const iconAllyInfo = props.icon ? getIconA11yInfo({
    icon: props.icon,
    tooltip: props.iconTooltip,
    ariaLabel: props.iconAriaLabel,
    ariaHidden: !!props.label && !!props.iconAriaHidden
  }) : undefined;
  if (props.readOnly) {
    // in this setup, make sure the icon, if available, and inner
    // text is also read by the SR
    if (ariaLabelledBy.length !== 0) {
      if (props.icon) ariaLabelledBy.push(itemIconId);
    }
    // note that ariaLabel is ignored by SR if already using ariaLabelledBy
    if (props.ariaLabel) {
      ariaLabel.push(props.ariaLabel);
      if (ariaLabelledBy.length === 0) {
        if (props.icon) {
          if (iconAllyInfo?.tooltip) ariaLabel.push(iconAllyInfo.tooltip);
        }
      }
    }
  }
  const setKeyboardTooltipRef = useKeyboardTooltip({
    traverseChildren: true
  });
  const itemIcon = props.icon ? /*#__PURE__*/_jsx("div", {
    className: cx(cn.itemIcon, !!!props.label && cn.iconOnly),
    children: /*#__PURE__*/_jsx(Icon, {
      id: itemIconId,
      icon: props.icon,
      ...iconAllyInfo,
      // Note for custom SVG, consumer will need add className="iconColor"
      // in rect element inside SVG for the disabled presentation.
      disabled: props.disabled,
      testId: postfixTestId(props.testId, 'itemIcon')
    })
  }) : undefined;

  /* NOTE: we can't use button on dropdown selectBox because it can't receive focus on FF+MacOs
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Clicking_and_focus */
  return props.readOnly ? /*#__PURE__*/_jsx("div", {
    ref: e => {
      setRef(props.innerRef, e);
      setKeyboardTooltipRef(e);
      setTruncationRef(e);
    },
    role: "textbox",
    "aria-disabled": props.disabled,
    "aria-describedby": ariaDescribedBy.length ? ariaDescribedBy.join(' ') : undefined,
    "aria-label": ariaLabel.join(' '),
    "aria-labelledby": ariaLabelledBy.length ? ariaLabelledBy.join(' ') : undefined,
    "aria-readonly": "true",
    title: props.tooltip,
    className: cx(props.className, cn.readOnly, cn.focusVisible, props.disabled && cn.disabled),
    tabIndex: focusable ? 0 : -1 // If set tabIndex to undefined rather than -1, it does not fire onFocus event at all
    ,
    "data-testid": props.testId,
    style: {
      ...tokens,
      ...props.style
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: cn.textWrapper,
      children: [itemIcon, /*#__PURE__*/_jsx("span", {
        id: innerTextId,
        ref: el => {
          setRef(props.textRef, el);
          setRef(textRef, el);
        },
        className: cx(cn.label, props.disabled && cn.labelDisabled, !!!props.label && cn.labelIconOnly),
        "data-testid": postfixTestId(props.testId, 'readOnlyValue'),
        children: props.label ?? ''
      })]
    })
  }) : /*#__PURE__*/_jsxs("div", {
    id: props.id,
    ref: e => {
      setRef(props.innerRef, e);
      setKeyboardTooltipRef(e);
      setTruncationRef(e);
    },
    role: "combobox",
    "aria-haspopup": "listbox",
    "aria-controls": props.listId,
    "aria-expanded": props.isOpen ? 'true' : 'false',
    "aria-activedescendant": props.ariaActiveDescendant,
    "aria-disabled": props.disabled,
    "aria-invalid": props.status === 'alert' ? true : undefined,
    "aria-describedby": ariaDescribedBy.length ? ariaDescribedBy.join(' ') : undefined,
    "aria-label": props.ariaLabel,
    "aria-labelledby": ariaLabelledBy.length ? ariaLabelledBy.join(' ') : undefined,
    title: props.tooltip,
    className: cx(props.className, cn.select, !props.isOpen && cn.focusVisible, props.disabled && cn.disabled, props.isOpen && cn.expanded, showStatus && !props.isOpen && cn.status),
    tabIndex: focusable ? 0 : -1 // If set tabIndex to undefined rather than -1, it does not fire onFocus event at all
    ,
    "data-testid": props.testId,
    style: {
      ...tokens,
      ...props.style
    },
    onClick: () => {
      props.toggleOpen?.();
      props.onPress?.();
    },
    onKeyDown: e => {
      if (e.key === ' ') {
        props.toggleOpen?.();
      }
      props.onKeyDown?.(e);
    },
    children: [props.required ? /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTextId,
      children: localize('required.aria')
    }) : null, /*#__PURE__*/_jsxs("div", {
      className: cn.textWrapper,
      "aria-live": !props.isOpen ? 'polite' : undefined,
      children: [itemIcon, /*#__PURE__*/_jsx("span", {
        id: innerTextId,
        ref: el => {
          setRef(props.textRef, el);
          setRef(textRef, el);
        },
        className: cx(props.disabled ? cn.labelDisabled : !showStatus && props.isPlaceholder ? cn.labelPlaceholder : cn.label, props.labelClassName, props.label === '' && cn.labelIconOnly),
        "data-testid": postfixTestId(props.testId, 'value'),
        children: props.label ?? ''
      })]
    }), props.status || showStatusIcon ? /*#__PURE__*/_jsx("div", {
      className: cn.statusIcon,
      ...(density === 'high' && {
        style: {
          '--icon-height': dt.tokens['size-icon-s'],
          '--icon-width': dt.tokens['size-icon-s']
        }
      }),
      children: statusIcon
    }) : null, renderSlotWithComponentFallback(props.renderIcon, Icon, {
      className: cn.selectIcon,
      disabled: props.disabled,
      testId: postfixTestId(props.testId, 'icon'),
      icon: DisclosureExpand,
      role: 'presentation'
    })]
  });
};
export const getSelectDesignTokens = (dt, opts) => {
  const getOverrides = opts?.theme;
  let tokens;
  if (getOverrides) {
    // Ensure that `--select-placeholder-fg` isn't being set to undefined to avoid
    // overriding the default placeholder foreground token
    const placeholderFg = getOverrides({
      state: 'placeholder'
    })?.fg;
    tokens = {
      ...renderDesignTokens(getOverrides({
        state: 'normal'
      }) ?? {}, 'select'),
      ...renderDesignTokens(getOverrides({
        state: 'hover'
      }) ?? {}, 'select-hover'),
      ...renderDesignTokens(getOverrides({
        state: 'active'
      }) ?? {}, 'select-active'),
      ...renderDesignTokens(placeholderFg ? {
        fg: placeholderFg
      } : {}, 'select-placeholder')
    };
  }
  const getStatusTokens = () => {
    switch (opts?.status) {
      case 'alert':
        return {
          'status-bd': dt.tokens['color-action-border-bottom-control-alert'],
          'status-bg': dt.tokens['color-action-background-control-alert']
        };
      case 'warning':
        return {
          'status-bd': dt.tokens['color-action-border-bottom-control-warning'],
          'status-bg': dt.tokens['color-action-background-control-warning']
        };
    }
  };
  const styleTokens = opts?.readOnly ? {
    bd: dt.tokens['color-action-border-control-read-only'],
    bg: dt.tokens['color-action-background-control-read-only'],
    fg: dt.tokens['color-action-text-control-read-only']
  } : {
    bd: dt.tokens['color-action-border-bottom-control-normal'],
    bg: dt.tokens['color-action-background-control-normal'],
    fg: dt.tokens['color-action-text-control-normal']
  };
  const densityFontTokens = opts?.density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m'];
  const densityTokens = opts?.density === 'high' ? {
    pd: `${dt.tokens['space-base-xxs']} ${dt.tokens['space-base-xs']}`,
    'min-block-size': '1.5rem'
  } : {
    pd: `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`,
    'min-block-size': '1.75rem'
  };
  return {
    ...renderDesignTokens({
      'font-size': opts?.font?.fontSize ?? densityFontTokens,
      ...getStatusTokens(),
      ...styleTokens,
      ...densityTokens,
      'status-pd': opts?.status ? dt.tokens['space-base-m'] : 0,
      'hover-fg': dt.tokens['color-action-text-control-hover'],
      'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
      'hover-bg': dt.tokens['color-action-background-control-hover'],
      'disabled-opacity': 1,
      'placeholder-fg': dt.tokens['color-action-text-control-placeholder'],
      'placeholder-active-fg': dt.tokens['color-action-text-control-placeholder'],
      'active-fg': dt.tokens['color-action-text-control-selected'],
      'active-bd': dt.tokens['color-action-border-bottom-control-selected'],
      'active-bg': dt.tokens['color-action-background-control-selected'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      'disabled-bg': dt.tokens['color-action-background-control-disabled'],
      'disabled-bd': dt.tokens['color-action-border-control-disabled'],
      'font-family': opts?.font?.fontFamily ?? 'inherit',
      'font-style': opts?.font?.fontStyle ?? 'inherit',
      'font-weight': opts?.font?.fontWeight ?? 'inherit',
      'text-decoration': opts?.font?.textDecoration ?? 'inherit',
      'spacing-xs': dt.tokens['space-base-xs'],
      'bd-width': `0 0 ${dt.tokens['size-border-width-s']} 0`,
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
    }, 'select'),
    ...tokens,
    ...(!dt.enableNovaTokens && getSelectLegacyDesignTokens(dt, opts))
  };
};
export const getSelectLegacyDesignTokens = (dt, opts) => {
  const getOverrides = opts?.theme;
  let tokens;
  if (getOverrides) {
    // Ensure that `--select-placeholder-fg` isn't being set to undefined to avoid
    // overriding the default placeholder foreground token
    const placeholderFg = getOverrides({
      state: 'placeholder'
    })?.fg;
    tokens = {
      ...renderDesignTokens(getOverrides({
        state: 'normal'
      }) ?? {}, 'select'),
      ...renderDesignTokens(getOverrides({
        state: 'hover'
      }) ?? {}, 'select-hover'),
      ...renderDesignTokens(getOverrides({
        state: 'active'
      }) ?? {}, 'select-active'),
      ...renderDesignTokens(placeholderFg ? {
        fg: placeholderFg
      } : {}, 'select-placeholder')
    };
  }
  const getStatusTokens = () => {
    switch (opts?.status) {
      case 'alert':
        return {
          'status-bd': dt.reportTokens.stateStatus.alert.bd
        };
      case 'warning':
        return {
          'status-bd': dt.reportTokens.stateStatus.warning.bd
        };
    }
  };
  const commonTokens = renderDesignTokens({
    'font-size': opts?.font?.fontSize ?? dt.reportTokens.font.size.m,
    ...getStatusTokens()
  }, 'select');
  switch (dt.baseReportThemeName) {
    case 'highcontrast2018':
    case 'highcontrast2017':
      return {
        ...renderDesignTokens({
          ...dt.reportTokens.stateStandard.normal,
          'hover-bd': dt.reportTokens.stateStandard.hover.bd,
          'hover-bg': dt.reportTokens.stateStandard.hover.bg,
          'hover-fg': dt.reportTokens.stateStandard.hover.fg,
          'active-bd': 'dt.reportTokens.stateStandard.normal.bd',
          'active-filter': dt.reportTokens.stateStandard.active.filter,
          'disabled-opacity': 0.3,
          'placeholder-fg': dt.c.n1,
          'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
          'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
          'disabled-bd': dt.reportTokens.stateStandard.disabled.bd
        }, 'select'),
        ...commonTokens,
        ...tokens
      };
  }
  switch (dt.themeVariant) {
    case 'dark':
      return {
        ...renderDesignTokens({
          ...dt.reportTokens.stateStandard.normal,
          'hover-bd': dt.reportTokens.stateStandard.hover.bd,
          'hover-bg': dt.reportTokens.stateStandard.hover.bg,
          'hover-fg': dt.reportTokens.stateStandard.hover.fg,
          'active-bd': dt.reportTokens.stateStandard.active.bd,
          'active-filter': dt.reportTokens.stateStandard.active.filter,
          'disabled-opacity': 0.3,
          'placeholder-fg': setAlpha(dt.c.white, 0.65),
          'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
          'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
          'disabled-bd': dt.reportTokens.stateStandard.disabled.bd
        }, 'select'),
        ...commonTokens,
        ...tokens
      };
    case 'high-contrast':
      return {
        ...renderDesignTokens({
          ...dt.reportTokens.stateStandard.normal,
          'hover-bd': dt.reportTokens.stateStandard.hover.bd,
          'hover-bg': dt.reportTokens.stateStandard.hover.bg,
          'hover-fg': dt.reportTokens.stateStandard.hover.fg,
          'active-bd': dt.reportTokens.stateStandard.normal.bd,
          'active-filter': dt.reportTokens.stateStandard.active.filter,
          'disabled-opacity': 0.5,
          'placeholder-fg': dt.c.n1,
          'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
          'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
          'disabled-bd': dt.reportTokens.stateStandard.disabled.bd
        }, 'select'),
        ...commonTokens,
        ...tokens
      };
    default:
      return {
        ...renderDesignTokens({
          ...dt.reportTokens.stateStandard.normal,
          'hover-bd': dt.reportTokens.stateStandard.hover.bd,
          'hover-bg': dt.reportTokens.stateStandard.hover.bg,
          'hover-fg': dt.reportTokens.stateStandard.hover.fg,
          'active-bd': dt.reportTokens.stateStandard.active.bd,
          'active-filter': dt.reportTokens.stateStandard.active.filter,
          'disabled-opacity': 0.3,
          'placeholder-fg': dt.c.n1,
          'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
          'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
          'disabled-bd': dt.reportTokens.stateStandard.disabled.bd
        }, 'select'),
        ...commonTokens,
        ...tokens
      };
  }
};
//# sourceMappingURL=SelectBoxDesign.js.map